import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { gridGapStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    errorHelpMessage = "errorHelpMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.errorHelpMessage]: string;
    };
}

export interface SideMenuStyles {
    container?: InjectableCss;
    grid?: GridContainerProps;
    signInGridItem?: GridItemProps;
    separatorGridItem?: GridItemProps;
    separator?: TypographyProps;
    createGridItem?: GridItemProps;
    spacerGridItem?: GridItemProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: StoreApplicationState) => {
    const settings = getStoreSettingsCollection(state);

    return {
        allowCreateAccount: settings.accountSettings.allowCreateAccount,
        hasExternalError: state.store.pages.signIn.hasExternalError,
    };
};

const styles = {
    container: {},
    grid: {
        gap: 0,
        css: css`
            ${gridGapStyles([], [TYPOGRAPHY.spacing.large])}
        `,
    },
    signInGridItem: {
        width: [12, 12, 5, 4, 4] as GridWidths[],
    },
    separatorGridItem: {
        width: [12, 12, 2, 2, 2] as GridWidths[],
        css: css`
            position: relative;

            &::before {
                content: "";
                position: absolute;
                top: 50%;
                right: 0;
                left: 0;
                height: 1px;
                background-color: ${getColor("common.border")};

                ${props =>
                    breakpointMediaQueries(
                        props.theme,
                        [
                            null,
                            null,
                            css`
                                top: 0;
                                right: auto;
                                bottom: 0;
                                left: 50%;
                                width: 1px;
                                height: auto;
                            `,
                        ],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    separator: {
        css: css`
            position: relative;
            margin: auto;
            background-color: ${getColor("common.background")};
            padding: 0 1em;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.smaller};

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            padding: 1em 0;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    createGridItem: {
        width: [12, 12, 5, 5, 4] as GridWidths[],
        css: css`
            flex-direction: column;
        `,
    },
    spacerGridItem: {
        width: [0, 0, 0, 1, 2] as GridWidths[],
    },
};
export const signInStyles = styles;

const SignInView: React.FunctionComponent<Props> = ({ id, allowCreateAccount, fields, hasExternalError }) => {
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.grid}>
                <GridItem {...styles.signInGridItem}>
                    <Zone contentId={id} zoneName="Left" />
                </GridItem>
                {allowCreateAccount ? (
                    <GridItem {...styles.separatorGridItem}>
                        <Typography {...styles.separator}>{translate("OR")}</Typography>
                    </GridItem>
                ) : (
                    <GridItem width={styles.separatorGridItem.width}></GridItem>
                )}
                <GridItem {...styles.createGridItem}>
                    {hasExternalError && fields.errorHelpMessage && parse(fields.errorHelpMessage, parserOptions)}
                    <Zone contentId={id} zoneName="Right" />
                </GridItem>
                <GridItem {...styles.spacerGridItem} />
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SignInView),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.errorHelpMessage,
                displayName: "Error Help Message",
                editorTemplate: "RichTextField",
                defaultValue: "<p><strong>For assistance please select the appropriate link below:</strong><br><strong>SCP/SPP Distributors in North America:</strong> <a href=\"https://www.pool360.com\">www.pool360.com</a><br><strong>Canada:</strong> <a href=\"https://www.pool360.ca\">www.pool360.ca</a><br><strong>France:</strong> <a href=\"https://www.pool360.eu\">www.pool360.eu</a><br><strong>Australia:</strong> <a href=\"https://www.pool360.au\">www.pool360.au</a></p>",
                fieldType: "Contextual",
                tooltip: "Instructions for the user when a Sign In error message is displayed"
            }
        ],
    },
};

export default widgetModule;
